import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';

import GraphVerticalBar from 'containers/ForecastDataVisualizer/GraphTooltips/GraphVerticalBar';
import type { TideLocation, TidePoint, MoveTideLabel } from 'types/tide';

import useSetupTideChart from '../../hooks/useSetupTideChart';

import styles from './TideChart.module.scss';

interface Props {
  data: Array<TidePoint>;
  tideLocation: TideLocation;
  units: string;
  dateFormat: 'MDY' | 'DMY';
  startDate: number;
  endDate: number;
  dayIndex: number;
  isTableMode?: boolean;
  showNowPosition?: boolean;
  moveTideLabel: MoveTideLabel;
}

const TideChart: React.FC<Props> = ({
  data,
  tideLocation,
  units,
  dateFormat,
  startDate,
  endDate,
  dayIndex = 0,
  isTableMode = false,
  showNowPosition = true,
  moveTideLabel,
}) => {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { height: graphHeight = 100, width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const { setupTideChart } = useSetupTideChart({
    dateFormat,
    data,
    tideLocation,
    units,
    startDate,
    endDate,
    graphHeight,
    graphWidth,
    dayIndex,
    moveTideLabel,
  });

  const leftNowPosition = useMemo(
    () =>
      isTableMode && dayIndex === 0 && graphWidth
        ? ((+new Date() - startDate) /
            ((data[data.length - 1]?.timestamp || 0) * 1000 - startDate)) *
          graphWidth
        : -1,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, startDate, graphWidth],
  );

  const debouncedSetupTideChart = useMemo(
    () => debounce(setupTideChart, 100, { leading: true, trailing: true }),
    [setupTideChart],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetupTideChart();

    return () => {
      debouncedSetupTideChart.cancel();
    };
  }, [graphWidth, debouncedSetupTideChart]);

  return (
    <div className={styles.tideChartContainer} data-testid="tide-chart-container">
      {showNowPosition && leftNowPosition >= 0 && graphWidth ? (
        <GraphVerticalBar
          leftPosition={leftNowPosition}
          timeLabel="Now"
          isStatic
          graphWidth={graphWidth}
        />
      ) : null}
      <div
        className={classNames({ [styles.tideChartCanvas]: true, 'sl-tide-chart-canvas': true })}
        id={`tide-chart-day-${dayIndex}`}
        ref={canvasRef}
      />
    </div>
  );
};

export default TideChart;
